import { Client } from "@/Common/Api/Client";
import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { ButtonControl } from "@/Common/Components/Buttons/ButtonControl";
import ButtonGroup from "@/Common/Components/Buttons/ButtonGroup";
import { DateSelector } from "@/Common/Components/Inputs/DateSelector";
import { SingleSelectAutoComplete } from "@/Common/Components/Inputs/SingleSelectAutocomplete";
import { Popup } from "@/Common/Components/Modals/Popup";
import { KeyValuePair } from "@/Common/Models/UI/KeyValuePair";
import * as LocalPatientOperations from "@/Common/Operations/operations.patient.local";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { IPatientRecallsModel } from "@/Patient/Models/UI/History/IPatientRecallsModel";
import { IPatientHistoryRecallsProps } from "./RecallsView";

class RecallNewEntryPopupState {
    public recallId: string;
    public description: string;
    public textNote: string;
    public dueDate: Date;
    public stateLookup: string;
    public selectedDescriptions: KeyValuePair<string, string>[];
    public isSent: boolean;

    public static getDefault(props: IPatientHistoryRecallsProps): RecallNewEntryPopupState {
        const state = new RecallNewEntryPopupState();
        state.recallId = props.Model.get("SelectedRecallId");
        state.stateLookup = props.Model.get("SelectedStatusId");
        state.textNote = null;
        state.dueDate = new Date(props.Model.get("DueDate"));
        state.description = props.Model.get("Description");
        state.selectedDescriptions = [];
        state.isSent = false;

        return state;
    }
}

class RecallNewEntryPopup extends BaseReactComponent<IPatientHistoryRecallsProps, RecallNewEntryPopupState> {
    constructor(props: IPatientHistoryRecallsProps) {
        super(props);
        this.state = RecallNewEntryPopupState.getDefault(props);
    }

    public render(): JSX.Element {
        var recallModel: IPatientRecallsModel;
        if (this.props.Model)
            recallModel = this.props.Model;

        const hasDescription = this.state.description.trim().length > 0;
        const hasDueDate = !!this.state.dueDate;

        return (
            <Popup
                manageOverlay
                title={cacheStore.GetString("NewEntryLabel")}
                cssClass="double-booking centered-popup"
                closeTarget={LocalPatientOperations.History.Recall_CancelNewEntry}
            >
                <div className="p-body" onClick={e => e.stopPropagation()}>
                    <div>
                        <h4 className="thin-font">{cacheStore.GetString("DescriptionLabel").toUpperCase()}</h4>
                        <div>
                            <SingleSelectAutoComplete
                                id={this.state.recallId}
                                valid={hasDescription}
                                useblurevent
                                componentId="recall_patient_edit_description"
                                selectedItems={this.state.selectedDescriptions.toImmutable()}
                                displayedTextValue={this.state.description}
                                onSelecting={kvp => this.mutateState(s => s.description = kvp.Value)}
                                focusOnLoad
                                onChangeText={text => this.mutateState(s => s.description = text)}
                                closeDropDownAfterSelection
                            />
                        </div>
                    </div>

                    <div className="recall-note-paddingtop">
                        <h4 className="thin-font">{cacheStore.GetString("DueDateLabel").toUpperCase()}</h4>
                        <div>
                            <DateSelector
                                id="task_edit_duedate_note"
                                cssClass=""
                                date={this.state.dueDate}
                                onChangeCallback={date => this.mutateState(s => s.dueDate = date)}
                                dateFormat="yyyy/MM/dd"
                                disabled={false}
                                toDayEnabled
                                valid={hasDueDate}
                            />
                        </div>
                    </div>

                    <div className="recall-note-paddingtop">
                        <h4 className="thin-font">{cacheStore.GetString("StatusDLabel").toUpperCase()}</h4>
                        <select onChange={(e: any) => this.onStatusChange(e.target.value)} className="form-control myle-control" value={this.state.stateLookup}>
                            <option key="empty" value="" />
                            {recallModel.get("Statuses").toArray().map(s => (
                                <option key={s.LookupID} value={s.LookupID}> {cacheStore.GetLocalizedValueByLookupId(s.LookupID)}</option>
                            ))}
                        </select>
                    </div>

                    <div className="recall-note-paddingtop">
                        <h4 className="thin-font">{cacheStore.GetString("AddNoteLabel").toUpperCase()}</h4>
                        <textarea
                            className="form-control vresize"
                            onChange={(e: any) => this.onNotesChange(e.target.value)}
                            id="history_create_communications"
                            disabled={false}
                            rows={4}
                            style={{ maxHeight: "300px", marginTop: "5px" }}
                            value={this.state.textNote || ""}
                        />
                    </div>
                </div>
                <div className="p-footer flex-display flex-end">
                    <ButtonGroup>
                        <ButtonControl
                            onClick={() => sessionStore.dispatch(LocalPatientOperations.History.Recall_CancelNewEntry)}
                            label={cacheStore.GetString("CancelLabel")}
                            className="btn btn-default"
                        />
                        <ButtonControl
                            disabled={!hasDescription || !hasDueDate || this.state.isSent}
                            onClick={() => this.onCreateNewEntry()}
                            label={cacheStore.GetString("NoteSubmitLabel")}
                            className="btn btn-primary"
                        />
                    </ButtonGroup>
                </div>
            </Popup>
        );
    }

    public componentDidMount(): void {
        super.setOverlay("RecallNewEntryPopup", { transparent: true });

        $(window).trigger("resize");
    }

    private onStatusChange(value: string): void {
        this.mutateState(s => s.stateLookup = value);
    }

    private onNotesChange(text: string): void {
        this.mutateState(s => s.textNote = text);
    }

    private onCreateNewEntry(): void {
        this.setState({ isSent: true });
        Client.Postv2(
            ServerOperations.Server.Myle_Patient_History_CreateNewRecallEntry,
            {
                RecallId: this.state.recallId,
                Status: this.state.stateLookup,
                Note: this.state.textNote,
                Type: this.state.description,
                DueDate: this.state.dueDate
            },
            response => {
                if (response.IsSuccess)
                    sessionStore.dispatch(LocalPatientOperations.History.Recall_LoadSelectedRecall, { type: this.state.description, dueDate: this.state.dueDate });
            }
        );
    }
}

export { RecallNewEntryPopup };
